import type { PromoSummary } from '#root/api/clients/cart/data-contracts'
import type { ApproachingDiscount, ApproachingPromotion } from '#types/cart'

/**
 * Pinia store for managing approaching promotions state.
 *
 * Tracks promotions that are not yet activated but approaching their activation threshold,
 * displaying them as cart incentives to encourage customers to add more items. Supports
 * both discount promotions and Gift With Purchase (GWP) offers, with separate messaging
 * for guest vs. registered users.
 *
 * **Multi-brand behavior:** Promotion display logic is consistent across all brands.
 * Callout message format and i18n keys may vary by brand.
 *
 * @example
 * const store = useApproachingPromotionsStore()
 * // Process cart response
 * store.process({
 *   approachingDiscount: cartResponse.approachingDiscount,
 *   promoSummary: cartResponse.promoSummary
 * })
 * // Get promotions for cart page
 * const cartPromos = store.get('CART')
 */
export const useApproachingPromotionsStore = defineStore('approachingPromotions', () => {
  const { $formatPrice, $t } = useNuxtApp()
  const auth = useAuthStore()
  const approachingPromotions = ref<ApproachingPromotion[]>([])

  /**
   * Splits callout message string on pipe delimiters while preserving content inside braces.
   *
   * Used to parse promotion callout messages which contain 3 pipe-separated messages:
   * 1. Below threshold message
   * 2. Above threshold message (with distance placeholder)
   * 3. Threshold reached message
   *
   * Brace syntax `{sign-in|SignIn}` is preserved as a single token, not split.
   *
   * @param calloutMessage - Raw callout message from promotion config (optional)
   * @returns Array of message strings (empty if no message provided)
   * @example
   * splitMessages("Below|{value} away|Congrats")
   * // returns ["Below", "{value} away", "Congrats"]
   */
  const splitMessages = (calloutMessage?: string): string[] => {
    if (!calloutMessage) return []
    // split on pipes unless they are inside braces (e.g. `{sign-in|SignIn}`)
    return calloutMessage.match(/(?:\{[^{}]*\}|[^|{}])+/g)?.map((msg) => msg.trim()) || []
  }

  /**
   * Clears all approaching promotions from state.
   *
   * Called at the start of `process()` to reset before repopulating with fresh cart data.
   */
  const reset = () => {
    approachingPromotions.value = []
  }

  /**
   * Determines if a promotion should be shown based on user authentication state.
   *
   * Promotions with IDs containing `GUEST` are shown only to non-logged-in users.
   * Promotions with IDs containing `REGISTERED` are shown only to logged-in users.
   * All other promotions are shown to everyone.
   *
   * @param promoId - Promotion identifier from cart API
   * @returns `true` if promotion should be displayed to current user
   * @example
   * shouldIncludePromo("SPRING_SALE_GUEST") // true if not logged in
   * shouldIncludePromo("VIP_REGISTERED")    // true if logged in
   * shouldIncludePromo("FREESHIP")          // always true
   */
  const shouldIncludePromo = (promoId: string) => {
    if (promoId.includes('GUEST')) {
      return !auth.loggedIn
    }
    if (promoId.includes('REGISTERED')) {
      return auth.loggedIn
    }
    return true
  }

  /**
   * Checks if a promotion is a Gift With Purchase offer.
   *
   * @param promo - Promotion object from cart API
   * @returns `true` if promotion ID contains "GWP"
   * @example
   * isGiftWithPurchasePromotion({ promotionId: "SPRING_GWP_2024" }) // true
   * isGiftWithPurchasePromotion({ promotionId: "DISCOUNT20" })       // false
   */
  const isGiftWithPurchasePromotion = (promo: ApproachingDiscount | PromoSummary) => promo.promotionId.includes('GWP')

  /**
   * Returns the appropriate GWP message based on user authentication state.
   *
   * @param promo - Approaching discount object containing promotion details
   * @returns Localized message string with optional price placeholder replacement
   * @example
   * // For guest user with GWP_GUEST promotion
   * getGiftWithPurchaseMessage(promo)
   * // returns i18n value for approachingGiftWithProduct.guest
   *
   * // For generic GWP with threshold distance
   * getGiftWithPurchaseMessage({ distanceFromConditionThreshold: 2500 })
   * // returns i18n default message with "$25.00" interpolated
   */
  const getGiftWithPurchaseMessage = (promo: ApproachingDiscount) => {
    if (promo.promotionId.includes('GUEST')) {
      return $t.approachingGiftWithProduct.guest
    }
    else if (promo.promotionId.includes('REGISTERED')) {
      return $t.approachingGiftWithProduct.registered
    }
    else {
      return replaceAll($t.approachingGiftWithProduct.default, {
        distanceFromConditionThreshold: $formatPrice(promo.distanceFromConditionThreshold, useCurrencyCode())
      })
    }
  }

  /**
   * Processes unapplied promotions with unmet activation criteria.
   *
   * For each promotion:
   * 1. Filters by user eligibility (guest/registered)
   * 2. Determines appropriate message (GWP or standard discount)
   * 3. Adds to `approachingPromotions` state
   *
   * Standard discount promotions require exactly 3 pipe-separated messages in `calloutMsg`,
   * otherwise they are skipped as misconfigured.
   *
   * @param approachingDiscounts - Array of promotions from cart API (optional, defaults to empty)
   * @example
   * collectApproachingPromotions([{
   *   promotionId: "FREESHIP50",
   *   calloutMsg: "Free shipping|${distanceFromConditionThreshold} away|Free shipping earned",
   *   distanceFromConditionThreshold: 1500,
   *   conditionThreshold: 5000,
   *   promotionClass: "ORDER",
   *   approachingDisplayLevel: ["CART"]
   * }])
   */
  const collectApproachingPromotions = (approachingDiscounts: ApproachingDiscount[] = []) => {
    // process not yet applied promos with unmet activation criteria
    approachingDiscounts.forEach((promo) => {
      if (!shouldIncludePromo(promo.promotionId)) return

      const basePromotion: ApproachingPromotion = {
        contexts: promo.approachingDisplayLevel || [],
        promotionClass: promo.promotionClass,
        thresholdStatus: promo.distanceFromConditionThreshold ? 'Above Threshold' : 'Below Threshold',
        distanceFromConditionThreshold: promo.distanceFromConditionThreshold,
        conditionThreshold: promo.conditionThreshold,
        id: promo.promotionId,
        message: '',
      }

      if (isGiftWithPurchasePromotion(promo)) {
        basePromotion.message = getGiftWithPurchaseMessage(promo)
        if (basePromotion.message) {
          approachingPromotions.value.push(basePromotion)
        }
      }
      else {
        const messages = splitMessages(promo.calloutMsg)
        if (messages.length < 3) return // skip if promotion is misconfigured
        basePromotion.message = promo.distanceFromConditionThreshold && messages[1]
          ? replaceAll(messages[1], {
              distanceFromConditionThreshold: $formatPrice(promo.distanceFromConditionThreshold, useCurrencyCode())
            })
          : messages[0] || ''
        approachingPromotions.value.push(basePromotion)
      }
    })
  }

  /**
   * Processes already-applied promotions that have reached their activation threshold.
   *
   * For each applied promotion:
   * 1. Filters by user eligibility (guest/registered)
   * 2. Sets `thresholdStatus` to "Threshold Reached" and `applied: true`
   * 3. Uses the 3rd message from `calloutMessage` (earned message) or GWP earned i18n key
   * 4. Adds to `approachingPromotions` state
   *
   * Standard discount promotions require exactly 3 pipe-separated messages in `calloutMessage`,
   * otherwise they are skipped as misconfigured.
   *
   * @param promoSummary - Array of applied promotions from cart API (optional, defaults to empty)
   * @example
   * collectAppliedPromotions([{
   *   promotionId: "FREESHIP50",
   *   calloutMessage: "Free shipping|${distanceFromConditionThreshold} away|Free shipping earned",
   *   level: "ORDER",
   *   approachingDisplayLevel: ["CART"]
   * }])
   */
  const collectAppliedPromotions = (promoSummary: PromoSummary[] = []) => {
    promoSummary.forEach((promo) => {
      if (!shouldIncludePromo(promo.promotionId)) return

      const basePromotion: ApproachingPromotion = {
        contexts: promo.approachingDisplayLevel || [],
        id: promo.promotionId,
        promotionClass: promo.level,
        thresholdStatus: 'Threshold Reached',
        applied: true,
        message: '',
      }
      if (isGiftWithPurchasePromotion(promo)) {
        approachingPromotions.value.push({
          ...basePromotion,
          message: $t.approachingGiftWithProduct.earned,
        })
      }
      else {
        if (!promo?.calloutMessage) return
        const messages = splitMessages(promo.calloutMessage)
        if (messages.length !== 3) return // skip if promotion is misconfigured
        approachingPromotions.value.push({
          ...basePromotion,
          message: messages[2] || ''
        })
      }
    })
  }

  /**
   * Processes cart promotion data and updates store state.
   *
   * Workflow:
   * 1. Resets all existing promotions (`reset()`)
   * 2. Collects unapplied approaching promotions (`collectApproachingPromotions`)
   * 3. Collects already-applied promotions (`collectAppliedPromotions`)
   *
   * Call this after every cart API response to refresh promotion state.
   *
   * @param params - Object containing promotion arrays from cart API
   * @param params.approachingDiscount - Unapplied promotions with unmet criteria
   * @param params.promoSummary - Already-applied promotions
   * @example
   * const cartStore = useCartStore()
   * const approachingStore = useApproachingPromotionsStore()
   *
   * watchEffect(() => {
   *   if (cartStore.cart) {
   *     approachingStore.process({
   *       approachingDiscount: cartStore.cart.approachingDiscount || [],
   *       promoSummary: cartStore.cart.promotionSummary || []
   *     })
   *   }
   * })
   */
  const process = ({ approachingDiscount, promoSummary }: {
    approachingDiscount: ApproachingDiscount[]
    promoSummary: PromoSummary[]
  }) => {
    reset()
    collectApproachingPromotions(approachingDiscount)
    collectAppliedPromotions(promoSummary)
  }

  /**
   * Retrieves promotions filtered by display context.
   *
   * Display contexts control where promotions appear (e.g., CART, CHECKOUT, PDP).
   * Promotions with empty `contexts` arrays are shown everywhere.
   *
   * @param context - Display context filter (optional, defaults to all promotions)
   * @returns Array of promotions matching the context
   * @example
   * // Get all cart-page promotions
   * const cartPromos = store.get('CART')
   *
   * // Get all promotions regardless of context
   * const allPromos = store.get()
   */
  const get = (context?: ApproachingDiscount['approachingDisplayLevel'][number]) => context
    ? approachingPromotions.value.filter(({ contexts }) => !contexts.length || contexts.includes(context))
    : approachingPromotions.value

  /**
   * Removes a promotion from state by ID.
   *
   * Used for dismissible promotion banners. Does nothing if ID is not found.
   *
   * @param id - Promotion ID to remove
   * @example
   * // User clicks dismiss button on promotion banner
   * const handleDismiss = (promoId: string) => {
   *   store.dismiss(promoId)
   * }
   */
  const dismiss = (id: string) => {
    const promo = approachingPromotions.value.find((promo) => promo.id === id)
    if (!promo) return
    approachingPromotions.value.splice(approachingPromotions.value.indexOf(promo), 1)
  }

  /**
   * @returns Object containing all store actions and computed getters
   */
  return {
    dismiss,
    get,
    process,
    reset,
    splitMessages,
  }
})
